/**
 * Dock Layout Store
 * Global docking registry shared by the sidepanels AND the toolbar: for each
 * screen edge (left/right/top/bottom) it keeps the ordered list of docked
 * entries. An entry id is either the fixed 'toolbar' rail or a sidepanel
 * instance id ('main-panel', 'panel-xxxx'). Rendering walks these strips, so
 * two panels can sit side by side on one edge and a panel (or the toolbar)
 * can be dropped between, before or after any of them.
 *
 * Persisted under the panel storage namespace; survives restarts like the
 * rest of the layout stores. The store is a LAZY singleton: it is created on
 * first access so the namespace — locked in by `createPanelRegistry` at
 * module scope — is already fixed when the persist key resolves.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { debouncedLocalStorage } from './persistStorage';
import { panelStorageKey } from './namespace';

type DockLayoutStoreApi = ReturnType<typeof createDockLayoutStore>;

const createDockLayoutStore = () =>
    create<DockLayoutState>()(
        persist(
            (set, get) => ({
                strips: DEFAULT_DOCK_STRIPS,

                dockEntry: (entryId, target) =>
                    set((state) => {
                        const next: Record<DockSide, DockEntryId[]> = {
                            left: [...state.strips.left],
                            right: [...state.strips.right],
                            top: [...state.strips.top],
                            bottom: [...state.strips.bottom],
                        };
                        // Detach from the current strip first so a same-side move
                        // reinserts cleanly
                        (Object.keys(next) as DockSide[]).forEach((side) => {
                            next[side] = next[side].filter(
                                (id) => id !== entryId,
                            );
                        });
                        const strip = next[target.side];
                        // Clamp, so sentinel/past-end indexes land on the final
                        // slot (right/bottom edge-band drops dock innermost)
                        const index = Math.max(
                            0,
                            Math.min(target.index, strip.length),
                        );
                        strip.splice(index, 0, entryId);
                        return { strips: next };
                    }),

                removeEntry: (entryId) =>
                    set((state) => ({
                        strips: {
                            left: state.strips.left.filter(
                                (id) => id !== entryId,
                            ),
                            right: state.strips.right.filter(
                                (id) => id !== entryId,
                            ),
                            top: state.strips.top.filter(
                                (id) => id !== entryId,
                            ),
                            bottom: state.strips.bottom.filter(
                                (id) => id !== entryId,
                            ),
                        },
                    })),

                resetDockLayout: () => set({ strips: DEFAULT_DOCK_STRIPS }),
            }),
            {
                name: panelStorageKey('dock_layout'),
                storage: createJSONStorage(() => debouncedLocalStorage()),
                partialize: (state) => ({ strips: state.strips }),
                onRehydrateStorage: () => (state) => {
                    // Migration needs the rehydrated (or default) strips + the
                    // legacy toolbar payload; run once after hydration settles
                    if (state) {
                        const seeded = migrateToolbarPosition(state.strips);
                        if (seeded !== state.strips) {
                            getStore().setState({ strips: seeded });
                        }
                    }
                },
                merge: (persisted, current) => {
                    const saved = (
                        persisted as {
                            strips?: Record<DockSide, DockEntryId[]>;
                        }
                    )?.strips;
                    if (!saved) return current;
                    return {
                        ...current,
                        strips: {
                            left: saved.left ?? current.strips.left,
                            right: saved.right ?? current.strips.right,
                            top: saved.top ?? current.strips.top,
                            bottom: saved.bottom ?? current.strips.bottom,
                        },
                    };
                },
            },
        ),
    );

export type DockSide = 'left' | 'right' | 'top' | 'bottom';

/** A drop decision produced by the shared dock target resolver */
export interface DockTarget {
    side: DockSide;
    /** Insert position inside the side's strip (0 = outermost edge) */
    index: number;
}

export type DockEntryId = string; // 'toolbar' | sidepanel instance ids

/** Ordered entry ids per screen edge. Vertical strips (left/right) flow from
    the screen edge inward; horizontal strips (top/bottom) flow from the top /
    left edge inward. `resolveDockTarget` produces indexes against this order. */
interface DockLayoutState {
    /** Ordered entry ids per screen edge */
    strips: Record<DockSide, DockEntryId[]>;
    /** Move/insert an entry into a side strip at the given index */
    dockEntry: (entryId: DockEntryId, target: DockTarget) => void;
    /** Drop an entry from every strip (panel closed) */
    removeEntry: (entryId: DockEntryId) => void;
    /** Back to the factory arrangement (toolbar left, main panel right) */
    resetDockLayout: () => void;
}

export const DEFAULT_DOCK_STRIPS: Record<DockSide, DockEntryId[]> = {
    left: ['toolbar'],
    right: ['main-panel'],
    top: [],
    bottom: [],
};

/** One-time migration: toolbar edge used to live in the toolbar
    customization store; honor it the first time the dock store boots. */
const migrateToolbarPosition = (
    strips: Record<DockSide, DockEntryId[]>,
): Record<DockSide, DockEntryId[]> => {
    try {
        const raw = localStorage.getItem(
            panelStorageKey('toolbar_customization'),
        );
        if (!raw) return strips;
        const saved = JSON.parse(raw);
        const side = saved?.state?.position as DockSide | undefined;
        if (
            side === 'left' ||
            side === 'right' ||
            side === 'top' ||
            side === 'bottom'
        ) {
            const next: Record<DockSide, DockEntryId[]> = {
                left: strips.left.filter((id) => id !== 'toolbar'),
                right: strips.right.filter((id) => id !== 'toolbar'),
                top: strips.top.filter((id) => id !== 'toolbar'),
                bottom: strips.bottom.filter((id) => id !== 'toolbar'),
            };
            next[side] = ['toolbar', ...next[side]];
            return next;
        }
    } catch {
        // Corrupt payload keeps the defaults
    }
    return strips;
};

let instance: DockLayoutStoreApi | null = null;
const getStore = (): DockLayoutStoreApi => (instance ??= createDockLayoutStore());

/** Namespace-lazy singleton: call sites keep the plain
 * `useDockLayoutStore((s) => …)` / `.getState()` shape. */
export const useDockLayoutStore: DockLayoutStoreApi = ((...args: unknown[]) =>
    (getStore() as unknown as (...a: unknown[]) => unknown)(...args)) as DockLayoutStoreApi;
useDockLayoutStore.getState = () => getStore().getState();
useDockLayoutStore.setState = ((...args: unknown[]) =>
    (
        getStore().setState as unknown as (...a: unknown[]) => unknown
    )(...args)) as DockLayoutStoreApi['setState'];
useDockLayoutStore.subscribe = ((...args: unknown[]) =>
    (
        getStore().subscribe as unknown as (...a: unknown[]) => unknown
    )(...args)) as DockLayoutStoreApi['subscribe'];

/** The edge an entry is currently docked to (null when not docked) */
export const getDockEntrySide = (entryId: DockEntryId): DockSide | null => {
    const { strips } = useDockLayoutStore.getState();
    for (const side of Object.keys(strips) as DockSide[]) {
        if (strips[side].includes(entryId)) return side;
    }
    return null;
};
